import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, ShieldAlert, Check } from "lucide-react";

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const forced = !!user?.must_change_password;

  const lenOk = nw.length >= 8;
  const matchOk = nw.length > 0 && nw === cf;
  const diffOk = nw.length === 0 || nw !== cur;
  const canSubmit = cur.length > 0 && lenOk && matchOk && diffOk;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password aggiornata. Accedi con la nuova password.");
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Cambio password non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const Rule = ({ ok, text }) => (
    <li className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-500" : "text-muted-foreground"}`}>
      <Check className={`h-3.5 w-3.5 ${ok ? "opacity-100" : "opacity-30"}`} /> {text}
    </li>
  );

  return (
    <div className="mx-auto max-w-md" data-testid="change-password-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight">Cambia password</h1>
          <p className="text-sm text-muted-foreground">Aggiorna la password del tuo account</p>
        </div>
      </div>

      {forced && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[13px] text-amber-600 dark:text-amber-400" data-testid="change-password-forced">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Per motivi di sicurezza devi impostare una nuova password prima di continuare a usare la piattaforma.</span>
        </div>
      )}

      <Card className="p-6">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[11px] font-bold uppercase text-muted-foreground">Password attuale</Label>
            <div className="relative mt-1.5">
              <Input type={show ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)}
                     autoComplete="current-password" placeholder="La tua password attuale" data-testid="cp-current" className="pr-10" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="cp-toggle-visibility" tabIndex={-1}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase text-muted-foreground">Nuova password</Label>
            <Input type={show ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)}
                   autoComplete="new-password" placeholder="Almeno 8 caratteri" data-testid="cp-new" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase text-muted-foreground">Conferma nuova password</Label>
            <Input type={show ? "text" : "password"} value={cf} onChange={(e) => setCf(e.target.value)}
                   autoComplete="new-password" placeholder="Ripeti la nuova password" data-testid="cp-confirm" className="mt-1.5" />
          </div>

          <ul className="space-y-1 rounded-md bg-muted/40 p-3">
            <Rule ok={lenOk} text="Almeno 8 caratteri" />
            <Rule ok={matchOk} text="Le due nuove password coincidono" />
            <Rule ok={diffOk && nw.length > 0} text="Diversa dalla password attuale" />
          </ul>

          <Button type="submit" className="w-full" disabled={!canSubmit || busy} data-testid="cp-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
            Aggiorna password
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">Dopo il cambio dovrai accedere di nuovo con la nuova password.</p>
        </form>
      </Card>
    </div>
  );
}
